export interface ListenerBadge {
  name: string;
  icon: string;
  color: string;
  description: string;
}

export interface FavoriteRadioEntry {
  id: number;
  stationName: string;
  frequency: string;
  category: string;
  logoUrl: string;
  isLive: boolean;
}

export interface FavoriteProgramEntry {
  id: number;
  programName: string;
  stationName: string;
  hostName: string;
  schedule: string;
  category: string;
}

export interface FavoriteHostEntry {
  id: number;
  name: string;
  stationName: string;
  role: string;
  photoUrl: string;
  isLive: boolean;
}

export interface RecentActivityEntry {
  id: number;
  type: 'escucho' | 'mensaje' | 'favorito' | 'evento' | 'insignia' | 'concurso';
  description: string;
  stationName: string | null;
  timestamp: string;
  icon: string;
  color: string;
}

export interface ListenerProfileIdentity {
  photo: string;
  name: string;
  username: string;
  city: string;
  country: string;
  joinDate: string;
  bio: string;
  level: {
    name: string;
    number: number;
    progress: number;
    nextLevel: string;
    pointsToNext: number;
  };
  stats: {
    totalListeningHours: number;
    messagesSent: number;
    radiosFavorited: number;
    radiosFollowed: number;
    eventsAttended: number;
  };
  badges: ListenerBadge[];
  favoriteRadios: FavoriteRadioEntry[];
  favoritePrograms: FavoriteProgramEntry[];
  favoriteHosts: FavoriteHostEntry[];
  recentActivity: RecentActivityEntry[];
}

export const listenerProfileIdentity: ListenerProfileIdentity = {
  photo: "https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20portrait%20with%20friendly%20warm%20smile%20casual%20modern%20style%20wearing%20simple%20olive%20green%20henley%20shirt%20clean%20soft%20gradient%20warm%20cream%20background%20with%20gentle%20natural%20lighting%20approachable%20authentic%20headshot%20photography%20editorial%20portrait%20style%20with%20shallow%20depth%20of%20field%20and%20soft%20bokeh%20background&width=400&height=400&seq=oyente-perfil-foto-01&orientation=squarish",
  name: "Marco Antonio Rivera",
  username: "@marco_oyente",
  city: "Ciudad de México",
  country: "México",
  joinDate: "2020-03-15",
  bio: "Amante de la música regional mexicana, el rock clásico y la buena radio. Oyente fiel desde 2020. Siempre con la radio encendida mientras trabajo. Coleccionista de playeras de estaciones de radio y fan de los maratones musicales de fin de semana.",
  level: {
    name: "Top Fan",
    number: 4,
    progress: 67,
    nextLevel: "VIP",
    pointsToNext: 1150,
  },
  stats: {
    totalListeningHours: 2847,
    messagesSent: 342,
    radiosFavorited: 8,
    radiosFollowed: 12,
    eventsAttended: 5,
  },
  badges: [
    {
      name: "Oyente Fiel",
      icon: "ri-heart-pulse-line",
      color: "bg-rose-100 text-rose-700",
      description: "Más de 1000 horas de escucha acumuladas",
    },
    {
      name: "Madrugador",
      icon: "ri-sun-line",
      color: "bg-amber-100 text-amber-700",
      description: "50 transmisiones sintonizadas antes de las 7 AM",
    },
    {
      name: "Mega Fan",
      icon: "ri-star-smile-line",
      color: "bg-amber-100 text-amber-700",
      description: "Sigue más de 10 radios activamente",
    },
    {
      name: "Comunicador",
      icon: "ri-chat-smile-2-line",
      color: "bg-emerald-100 text-emerald-700",
      description: "Más de 300 mensajes enviados al aire",
    },
    {
      name: "Oyente Legendario",
      icon: "ri-vip-crown-line",
      color: "bg-purple-100 text-purple-700",
      description: "Miembro activo por más de 5 años consecutivos",
    },
  ],
  favoriteRadios: [
    {
      id: 1,
      stationName: "La Mexicana FM",
      frequency: "103.50 RFW",
      category: "Regional Mexicana",
      logoUrl: "https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%20circular%20design%20warm%20orange%20and%20green%20colors%20clean%20modern%20broadcast%20style%20festive%20vibrant%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=200&height=200&seq=oyente-radio-fav-1&orientation=squarish",
      isLive: true,
    },
    {
      id: 2,
      stationName: "Rock Total Radio",
      frequency: "87.01 RFW",
      category: "Rock",
      logoUrl: "https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%20circular%20design%20black%20and%20red%20color%20scheme%20modern%20rock%20broadcast%20emblem%20energetic%20and%20bold%20style%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=200&height=200&seq=oyente-radio-fav-2&orientation=squarish",
      isLive: true,
    },
    {
      id: 3,
      stationName: "Deportes Total Radio",
      frequency: "120.00 RFW",
      category: "Deportes",
      logoUrl: "https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%20dynamic%20broadcast%20emblem%20energetic%20sports%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=200&height=200&seq=oyente-radio-fav-3&orientation=squarish",
      isLive: true,
    },
    {
      id: 4,
      stationName: "Radio Nueva Actitud",
      frequency: "94.70 RFW",
      category: "Pop",
      logoUrl: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%20clean%20circular%20emblem%20style%20warm%20golden%20and%20white%20color%20palette%20simple%20geometric%20shapes%20professional%20broadcast%20aesthetic%20isolated%20on%20transparent%20background%20high%20contrast%20vector%20style&width=200&height=200&seq=oyente-radio-fav-4&orientation=squarish",
      isLive: true,
    },
    {
      id: 5,
      stationName: "Banda Radio MX",
      frequency: "200.00 RFW",
      category: "Regional Mexicana",
      logoUrl: "https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%20circular%20design%20red%20white%20and%20green%20colors%20festive%20broadcast%20emblem%20traditional%20Mexican%20vibe%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=200&height=200&seq=oyente-radio-fav-5&orientation=squarish",
      isLive: true,
    },
  ],
  favoritePrograms: [
    {
      id: 1,
      programName: "El Sabor del Mediodía",
      stationName: "La Mexicana FM",
      hostName: "Alejandro Ramírez",
      schedule: "Lun-Vie 12:00 - 14:00",
      category: "Entretenimiento",
    },
    {
      id: 2,
      programName: "Rock Total en Vivo",
      stationName: "Rock Total Radio",
      hostName: "Diego \"El Lobo\" Vargas",
      schedule: "Lun-Jue 16:00 - 18:00",
      category: "Música",
    },
    {
      id: 3,
      programName: "La Mesa Redonda",
      stationName: "Deportes Total Radio",
      hostName: "Carlos Márquez",
      schedule: "Lun-Vie 12:00 - 14:00",
      category: "Deportes",
    },
    {
      id: 4,
      programName: "Noche de Estrenos",
      stationName: "Radio Nueva Actitud",
      hostName: "Valentina Suárez",
      schedule: "Mié-Sáb 20:00 - 22:00",
      category: "Música",
    },
    {
      id: 5,
      programName: "Tardes de Banda",
      stationName: "Banda Radio MX",
      hostName: "El Compa Luis",
      schedule: "Mar-Vie 15:00 - 17:00",
      category: "Música",
    },
  ],
  favoriteHosts: [
    {
      id: 1,
      name: "Alejandro Ramírez",
      stationName: "La Mexicana FM",
      role: "Locutor Principal",
      photoUrl: "https://readdy.ai/api/search-image?query=Friendly%20Hispanic%20male%20radio%20host%20portrait%20with%20warm%20confident%20smile%20wearing%20denim%20jacket%20over%20white%20shirt%20clean%20warm%20studio%20lighting%20on%20soft%20cream%20background%20approachable%20broadcaster%20headshot%20editorial%20portrait%20style&width=200&height=200&seq=oyente-host-fav-1&orientation=squarish",
      isLive: true,
    },
    {
      id: 2,
      name: "Diego \"El Lobo\" Vargas",
      stationName: "Rock Total Radio",
      role: "Conductor Estelar",
      photoUrl: "https://readdy.ai/api/search-image?query=Rock%20radio%20host%20male%20portrait%20with%20long%20dark%20hair%20and%20black%20leather%20jacket%20confident%20rebellious%20expression%20dark%20moody%20studio%20lighting%20with%20red%20accent%20dramatic%20broadcaster%20headshot%20editorial%20style&width=200&height=200&seq=oyente-host-fav-2&orientation=squarish",
      isLive: false,
    },
    {
      id: 3,
      name: "Carlos Márquez",
      stationName: "Deportes Total Radio",
      role: "Comentarista Deportivo",
      photoUrl: "https://readdy.ai/api/search-image?query=Sports%20commentator%20male%20portrait%20with%20energetic%20expression%20wearing%20navy%20blazer%20over%20white%20polo%20shirt%20professional%20studio%20portrait%20clean%20lighting%20on%20neutral%20gray%20background%20confident%20broadcaster%20headshot&width=200&height=200&seq=oyente-host-fav-3&orientation=squarish",
      isLive: true,
    },
    {
      id: 4,
      name: "Valentina Suárez",
      stationName: "Radio Nueva Actitud",
      role: "Presentadora",
      photoUrl: "https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20host%20portrait%20with%20bright%20genuine%20smile%20wearing%20casual%20pink%20blouse%20warm%20natural%20lighting%20on%20soft%20pastel%20background%20friendly%20approachable%20broadcaster%20headshot%20editorial%20portrait%20style&width=200&height=200&seq=oyente-host-fav-4&orientation=squarish",
      isLive: false,
    },
    {
      id: 5,
      name: "El Compa Luis",
      stationName: "Banda Radio MX",
      role: "Locutor",
      photoUrl: "https://readdy.ai/api/search-image?query=Mexican%20male%20radio%20host%20portrait%20with%20friendly%20big%20smile%20wearing%20cowboy%20hat%20and%20western%20style%20shirt%20warm%20golden%20studio%20lighting%20on%20cream%20background%20charismatic%20broadcaster%20headshot%20editorial%20portrait%20style&width=200&height=200&seq=oyente-host-fav-5&orientation=squarish",
      isLive: true,
    },
  ],
  recentActivity: [
    {
      id: 1,
      type: "escucho",
      description: "Escuchó El Sabor del Mediodía en La Mexicana FM",
      stationName: "La Mexicana FM",
      timestamp: "2026-06-08T09:30:00",
      icon: "ri-headphone-line",
      color: "bg-primary-100 text-primary-700",
    },
    {
      id: 2,
      type: "mensaje",
      description: "Envió un saludo al aire en La Mexicana FM",
      stationName: "La Mexicana FM",
      timestamp: "2026-06-08T10:15:00",
      icon: "ri-message-2-line",
      color: "bg-accent-100 text-accent-700",
    },
    {
      id: 3,
      type: "favorito",
      description: "Agregó a Banda Radio MX como radio favorita",
      stationName: "Banda Radio MX",
      timestamp: "2026-06-07T18:00:00",
      icon: "ri-heart-line",
      color: "bg-rose-100 text-rose-700",
    },
    {
      id: 4,
      type: "escucho",
      description: "Escuchó Rock Total en Vivo en Rock Total Radio",
      stationName: "Rock Total Radio",
      timestamp: "2026-06-07T16:45:00",
      icon: "ri-headphone-line",
      color: "bg-primary-100 text-primary-700",
    },
    {
      id: 5,
      type: "concurso",
      description: "Participó en el concurso semanal de Radio Nueva Actitud",
      stationName: "Radio Nueva Actitud",
      timestamp: "2026-06-06T20:00:00",
      icon: "ri-trophy-line",
      color: "bg-amber-100 text-amber-700",
    },
    {
      id: 6,
      type: "insignia",
      description: "Obtuvo la insignia Oyente Legendario por 5 años de fidelidad",
      stationName: null,
      timestamp: "2026-06-06T08:00:00",
      icon: "ri-award-line",
      color: "bg-purple-100 text-purple-700",
    },
    {
      id: 7,
      type: "escucho",
      description: "Escuchó Tardes de Banda en Banda Radio MX",
      stationName: "Banda Radio MX",
      timestamp: "2026-06-05T15:00:00",
      icon: "ri-headphone-line",
      color: "bg-primary-100 text-primary-700",
    },
    {
      id: 8,
      type: "evento",
      description: "Se registró para Fiesta de Aniversario de La Mexicana FM",
      stationName: "La Mexicana FM",
      timestamp: "2026-06-05T12:00:00",
      icon: "ri-calendar-check-line",
      color: "bg-emerald-100 text-emerald-700",
    },
    {
      id: 9,
      type: "mensaje",
      description: "Solicitó una canción en Rock Total Radio",
      stationName: "Rock Total Radio",
      timestamp: "2026-06-04T21:30:00",
      icon: "ri-message-2-line",
      color: "bg-accent-100 text-accent-700",
    },
    {
      id: 10,
      type: "escucho",
      description: "Escuchó Noche de Estrenos en Radio Nueva Actitud",
      stationName: "Radio Nueva Actitud",
      timestamp: "2026-06-04T20:00:00",
      icon: "ri-headphone-line",
      color: "bg-primary-100 text-primary-700",
    },
    {
      id: 11,
      type: "favorito",
      description: "Marcó como favorito el programa La Mesa Redonda",
      stationName: "Deportes Total Radio",
      timestamp: "2026-06-03T14:30:00",
      icon: "ri-heart-line",
      color: "bg-rose-100 text-rose-700",
    },
    {
      id: 12,
      type: "escucho",
      description: "Escuchó Mañana de Alabanza en Radio Fe y Esperanza",
      stationName: "Radio Fe y Esperanza",
      timestamp: "2026-06-03T08:00:00",
      icon: "ri-headphone-line",
      color: "bg-primary-100 text-primary-700",
    },
  ],
};